import { useState, useMemo } from 'react'
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ArrowDown, ArrowUp, ArrowUpDown, Download } from 'lucide-react'
import { ExportDataTable } from '@app/modules/Home/Data/components/DataLayerTableExport'
import { cn } from '@/lib/tailwind.utils'
import {
  SelectedElementProfile,
  SelectedElementProfileResponse,
  DamageState,
} from '../types/riskProfileSelected.types'
import { clientConfigAtom } from '@app/state/clientsAtom'
import { useAtomValue } from 'jotai'
import { formatLongCurrency } from '@app/utils/numbers.utils'
import { RiskProfileMultiDamageStates } from './RiskProfileMultiDamageStates'

type SelectedElementTableProps = {
  className?: string
  exposures: SelectedElementProfileResponse
}

const EXPORT_COLUMNS = [
  { id: 'group_name', header: 'Hazard' },
  { id: 'exposure', header: 'Exposure' },
  { id: 'damage_states', header: 'Damage State' },
  { id: 'damage_ratio', header: 'Damage Ratio' },
  { id: 'damage_cost', header: 'Damage Cost' },
]

export const SelectedElementTable = ({ className, exposures }: SelectedElementTableProps) => {
  const clientConfig = useAtomValue(clientConfigAtom)
  const [sorting, setSorting] = useState<SortingState>([])

  const COLUMNS: ColumnDef<SelectedElementProfile>[] = [
    {
      accessorKey: 'group_name',
      header: 'Hazard',
      cell: ({ getValue }) => {
        const name = getValue<string>().replace('_', ' ')

        return <p className="capitalize">{name}</p>
      },
    },
    {
      accessorKey: 'exposure',
      header: 'Exposure',
      cell: ({ getValue, row }) => {
        const exposure = getValue<number>().toFixed(2)
        const unit = row.original.exposure_unit ?? ''

        return exposure + ' ' + unit
      },
    },
    {
      accessorKey: 'damage_states',
      header: 'Damage States',
      cell: ({ getValue, row }) => {
        const damageStates = getValue<DamageState[]>()
        const rowId = row.id
        const highestDamageState = row.original.highest_damage_state ?? damageStates[0]

        return (
          <RiskProfileMultiDamageStates
            rowId={rowId}
            highestDamageState={highestDamageState}
            damageStates={damageStates}
          />
        )
      },
    },
    {
      accessorKey: 'damage_ratio',
      header: 'Damage Ratio',
      cell: ({ getValue }) => getValue<number>().toFixed(2),
    },
    {
      accessorKey: 'damage_cost',
      header: 'Damage Cost',
      cell: ({ getValue }) => formatLongCurrency(Number(getValue()), clientConfig?.locale),
    },
  ]

  const table = useReactTable({
    data: exposures,
    columns: COLUMNS,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
  })

  const exportRows = useMemo(
    () =>
      table.getPrePaginationRowModel().rows.map((row) =>
        row.getAllCells().reduce<Record<string, unknown>>((acc, cell) => {
          acc[cell.column.id] = cell.getValue()
          return acc
        }, {}),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sorting],
  )

  return (
    <div className={cn('flex flex-col gap-3 mt-2', className)}>
      <div className="flex items-center justify-between">
        <h2 className="text-h3 font-semibold text-rexBlue-900">Exposure across hazards</h2>

        <div className="flex justify-end">
          <ExportDataTable
            columns={EXPORT_COLUMNS}
            rows={exportRows}
            fileName="risk-profile-selected-elements.csv"
          >
            {({ onClick, disabled }) => (
              <button
                onClick={onClick}
                disabled={disabled}
                className="flex gap-2 items-center text-rexBlue hover:text-rexBlue-400 transition-colors"
              >
                <p>Export</p>
                <Download className="h-4 w-4 mt-[-2px]" />
              </button>
            )}
          </ExportDataTable>
        </div>
      </div>

      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader className="bg-[rgba(255,255,255,0.5)]">
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort()
                  const sortDir = header.column.getIsSorted()
                  return (
                    <TableHead key={header.id} className="font-semibold">
                      {canSort ? (
                        <button
                          className="flex items-center gap-1 hover:text-foreground"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sortDir === 'asc' && <ArrowUp className="h-3.5 w-3.5 flex-shrink-0" />}
                          {sortDir === 'desc' && (
                            <ArrowDown className="h-3.5 w-3.5 flex-shrink-0" />
                          )}
                          {sortDir === false && (
                            <ArrowUpDown className="h-3.5 w-3.5 opacity-50 flex-shrink-0" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="bg-white">
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
